import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Mail, MapPin, FileText, Link as LinkIcon, Download } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import RequestQuoteDialog from "@/components/affiliates/RequestQuoteDialog";
import { categoryLabel } from "@/lib/affiliates";

const AffiliateProfile = () => {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);

  const { data: affiliate, isLoading } = useQuery({
    queryKey: ["affiliate", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates_public" as any)
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
    enabled: !!slug,
  });

  const { data: publicEmail } = useQuery({
    queryKey: ["affiliate-public-email", slug],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_affiliate_public_email" as any, {
        _slug: slug!,
      });
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    enabled: !!slug && !!affiliate?.show_contact_email_public,
  });

  const { data: resources } = useQuery({
    queryKey: ["affiliate-resources", affiliate?.id],
    enabled: !!affiliate?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_resources")
        .select("id, title, description, resource_type, url, file_path, focus_area, created_at")
        .eq("partner_org_id", affiliate.id)
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        title: string;
        description: string | null;
        resource_type: string;
        url: string | null;
        file_path: string | null;
        focus_area: string | null;
      }>;
    },
  });

  const openResource = async (r: { url: string | null; file_path: string | null }) => {
    if (r.url) {
      window.open(r.url, "_blank", "noopener,noreferrer");
      return;
    }
    if (r.file_path) {
      const { data, error } = await supabase.storage
        .from("partner-resources")
        .createSignedUrl(r.file_path, 60 * 5);
      if (error || !data?.signedUrl) return;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <section className="pt-36 pb-10">
        <div className="container mx-auto px-6 max-w-4xl">
          <Link to="/affiliates/directory" className="text-sm text-primary hover:underline inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3 w-3" /> Back to directory
          </Link>

          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : !affiliate ? (
            <div className="text-center py-16">
              <h1 className="font-display text-2xl font-bold mb-2">Provider not found</h1>
              <p className="text-muted-foreground">This affiliate may have been removed or is not yet active.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
                <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {affiliate.logo_url ? (
                    <img src={affiliate.logo_url} alt={`${affiliate.name} logo`} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display text-3xl font-bold text-primary">{affiliate.name.slice(0, 1)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                    {categoryLabel(affiliate.category)}
                  </span>
                  <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 mb-2">{affiliate.name}</h1>
                  {affiliate.tagline && (
                    <p className="text-lg text-muted-foreground mb-3">{affiliate.tagline}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {affiliate.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {affiliate.location}</span>
                    )}
                    {affiliate.website && (
                      <a href={affiliate.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                        <ExternalLink className="h-3.5 w-3.5" /> Website
                      </a>
                    )}
                    {publicEmail && (
                      <a href={`mailto:${publicEmail}`} className="inline-flex items-center gap-1 hover:text-primary">
                        <Mail className="h-3.5 w-3.5" /> Email
                      </a>
                    )}
                  </div>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                  Request a Quote
                </Button>
              </div>

              {affiliate.description && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <h2 className="font-display text-lg font-semibold mb-3">About</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {affiliate.description}
                  </p>
                </div>
              )}

              {affiliate.services?.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <h2 className="font-display text-lg font-semibold mb-3">Services</h2>
                  <div className="flex flex-wrap gap-2">
                    {affiliate.services.map((s) => (
                      <span key={s} className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {affiliate.specialties?.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="font-display text-lg font-semibold mb-3">Specialties</h2>
                  <div className="flex flex-wrap gap-2">
                    {affiliate.specialties.map((s) => (
                      <span key={s} className="text-xs bg-muted text-muted-foreground px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {resources && resources.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6 mt-6">
                  <h2 className="font-display text-lg font-semibold mb-3">Resources</h2>
                  <div className="grid gap-2">
                    {resources.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => openResource(r)}
                        className="w-full text-left flex items-start gap-3 border border-border rounded-lg px-3 py-2 hover:bg-muted/30 transition"
                      >
                        <span className="mt-0.5 text-muted-foreground">
                          {r.resource_type === "file" ? <FileText className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium truncate">{r.title}</span>
                          {r.description && (
                            <span className="block text-xs text-muted-foreground line-clamp-2">{r.description}</span>
                          )}
                        </span>
                        {r.focus_area && (
                          <span className="text-[10px] uppercase tracking-wider text-primary shrink-0">{r.focus_area}</span>
                        )}
                        {r.resource_type === "file" && <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <RequestQuoteDialog
                open={open}
                onOpenChange={setOpen}
                affiliateId={affiliate.id}
                affiliateName={affiliate.name}
              />
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AffiliateProfile;